import React, { useMemo } from "react";
import {
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { StyledCard } from "../styled";

const BarsReviewTable = ({
  osmRawData,
  barsToExclude,
  conflicts,
  onToggleBarExclusion,
  onIncludeAll,
  onExcludeAll,
  regions,
  assignRegionId,
  onAssignRegionChange,
  disableRegionSelect,
  blacklistUnincluded,
  onToggleBlacklistUnincluded
}) => {
  const sortedBars = useMemo(() => {
    const list = [...osmRawData];
    list.sort((a, b) => {
      const nameA = (a.bar_name || "").toLowerCase();
      const nameB = (b.bar_name || "").toLowerCase();
      if (nameA === nameB) {
        return 0;
      }
      return nameA < nameB ? -1 : 1;
    });
    return list;
  }, [osmRawData]);

  const excludedWithinView = useMemo(
    () => sortedBars.filter((bar) => barsToExclude.has(bar.id)).length,
    [barsToExclude, sortedBars]
  );

  return (
    <StyledCard>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h6">
          Bars to Upload ({sortedBars.length - excludedWithinView} of {sortedBars.length})
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="assign-region-select-label">Assign Region</InputLabel>
          <Select
            labelId="assign-region-select-label"
            label="Assign Region"
            value={assignRegionId}
            onChange={onAssignRegionChange}
            disabled={disableRegionSelect}
          >
            <MenuItem value="">No region</MenuItem>
            {regions
              .filter((region) => region.Name && typeof region.Name === "string")
              .map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.Name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
          <Box sx={{ flexGrow: 1, minWidth: 16 }} />
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip
            title="Blacklisted Locations won't show up on future lists when adding bars. These records can be removed from the Blacklist in Blacklisted tabs in the admin section."
            placement="top"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={blacklistUnincluded}
                    onChange={(e) => onToggleBlacklistUnincluded?.(e.target.checked)}
                    color="primary"
                  />
                )}
                label="Blacklist Unincluded Records"
              />
            </Box>
          </Tooltip>
        </Box>
        {barsToExclude.size > 0 && (
          <Button size="small" onClick={onIncludeAll} color="secondary">
            Include All
          </Button>
        )}
      </Box>

      <Box sx={{ overflowX: "auto", maxHeight: "600px", overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ backgroundColor: "white" }}>
                <Checkbox
                  checked={excludedWithinView === 0}
                  indeterminate={excludedWithinView > 0 && excludedWithinView < sortedBars.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onIncludeAll();
                    } else {
                      onExcludeAll();
                    }
                  }}
                />
              </TableCell>
              <HeaderCell>Bar Name</HeaderCell>
              <HeaderCell>Address</HeaderCell>
              <HeaderCell>Type</HeaderCell>
              <HeaderCell>Phone</HeaderCell>
              <HeaderCell>Website</HeaderCell>
              <HeaderCell>Status</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedBars.map((bar) => {
              const isExcluded = barsToExclude.has(bar.id);
              const hasConflict = conflicts.some((conflict) => conflict.osmRecordId === bar.id);

              return (
                <TableRow
                  key={bar.id}
                  sx={{
                    backgroundColor: isExcluded ? "#ffebee" : hasConflict ? "#fff3e0" : "white",
                    opacity: isExcluded ? 0.6 : 1,
                    "&:hover": {
                      backgroundColor: isExcluded ? "#ffcdd2" : hasConflict ? "#ffe0b2" : "#f5f5f5"
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={!isExcluded} onChange={() => onToggleBarExclusion(bar.id)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: isExcluded ? "normal" : "500" }}>
                      {bar.bar_name || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      {bar.bar_address || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      {bar.bar_type || bar.osm_metadata?.amenity || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      {bar.bar_phone || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem", wordBreak: "break-all" }}>
                      {bar.bar_url ? (
                        <a href={bar.bar_url} target="_blank" rel="noopener noreferrer">
                          {bar.bar_url.substring(0, 30)}...
                        </a>
                      ) : (
                        "—"
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isExcluded ? (
                      <Chip label="Excluded" size="small" color="error" />
                    ) : hasConflict ? (
                      <Chip label="Conflict Resolved" size="small" color="warning" />
                    ) : (
                      <Chip label="New" size="small" color="success" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  </StyledCard>
  );
};

const HeaderCell = ({ children }) => (
  <TableCell sx={{ fontWeight: "bold", backgroundColor: "white" }}>{children}</TableCell>
);

export default BarsReviewTable;
